import type {ReactNode} from 'react';

import {createSignal} from '@pexip/signal';

import type {ColorScheme} from '../../../types/variants';
import type {ToastPosition} from '../Toast/Toast';

export type NotificationToastMessage = {
    colorScheme?: ColorScheme;
    enhanceStart?: React.ReactNode;
    isClickable?: boolean;
    isDanger?: boolean;
    /**
     * A toast that can interrupt will jump the queue and remove the
     * currently rendering toast and be rendered instead of it. Currently
     * rendering toasts that have a timeout of 0 cannot be interrupted, the
     * toast that tries to interrupt it is discarded and not queued.
     *
     * An example when this can be useful: if many
     * toasts are sent rapidly in response to UI state changes;
     * User mutes, unmute & mutes again and toasts are displayed to reflect these states,
     * the toasts will display without a long timeout between each.
     */
    isInterrupt?: boolean;
    message: ReactNode;
    position?: ToastPosition;
    testid?: string;
    timeout?: number;
    onDismiss?: () => void;
};

export type NotificationToastMessageSignal = NotificationToastMessage[];

/*
 * A toast message that has a timeout of 0 stays open
 * until you emit a close signal.
 */
type NotificationToastCloseSignal = {close: true};

export type NotificationToastSignal =
    NotificationToastMessageSignal | NotificationToastCloseSignal;

/** Toast options that never hold user-visible text. */
const LOGGED_TOAST_FIELDS = new Set<string>([
    'colorScheme',
    'isClickable',
    'isDanger',
    'isInterrupt',
    'position',
    'testid',
    'timeout',
]);

const isLoggedOption = ([key, value]: [string, unknown]) =>
    LOGGED_TOAST_FIELDS.has(key) &&
    ['string', 'number', 'boolean'].includes(typeof value);

/**
 * Describes a toast for logs. Its message and decorations can quote
 * participant names and chat text, so only its options are kept.
 */
const describeToast = (toast: unknown) => ({
    message: '[Toast text omitted]',
    ...(typeof toast === 'object' && toast !== null
        ? Object.fromEntries(Object.entries(toast).filter(isLoggedOption))
        : {}),
});

export const notificationToastSignal = createSignal<NotificationToastSignal>({
    name: 'notification:toast',
    describeSubject: subject => {
        if (Array.isArray(subject)) {
            return subject.map(describeToast);
        }
        return typeof subject === 'object' &&
            subject !== null &&
            'close' in subject &&
            subject.close === true
            ? {close: true}
            : describeToast(subject);
    },
});
